import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CreditCardValidators } from 'angular-cc-library';
import { LgvLoanApplicationService } from '../../../core/services/lgv-loan-application.service';
import { BehaviorSubject, catchError, of, Subject, take, takeUntil } from 'rxjs';
import { LgvTSHelper, NotificationService } from '../../../core/core.module';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'lgv-credit-report-dialog',
    templateUrl: './credit-report-dialog.component.html',
    styleUrls: ['./credit-report-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreditReportDialogComponent implements OnInit, OnDestroy {
    onDestroy$ = new Subject();
    creditReportForm: UntypedFormGroup;
    loanApp: any;
    taskId = '';
    taskItem = null;
    clientId = '';
    isLoading$ = new BehaviorSubject(false);
    creditReportTotalCost$ = new BehaviorSubject(null);
    get isSoftPull() {
        return this.taskItem?.creditReportType?.code === 'SoftPull';
    }

    get isHardPull() {
        return this.taskItem?.creditReportType?.code === 'HardPull';
    }

    get isBorrowersChoicePull () {
        return this.taskItem?.creditReportType?.code === 'BorrowersChoicePull';
    }

    get hasNoCreditReport() {
        return !this.taskItem?.creditReportType;
    }

    get ssnBorrowerCtrl() { return this.creditReportForm?.controls?.borrowers?.get('ssn') };
    get ssnCoborrowerCtrl() { return this.creditReportForm?.controls?.borrowers?.get('coBorrower')?.get('ssn') };
    get currentAddressStreetCtrl() { return this.creditReportForm?.controls?.borrowers?.get('currentAddress').get('street') };
    get currentAddressCityCtrl() { return this.creditReportForm?.controls?.borrowers?.get('currentAddress').get('city') };
    get currentAddressStateCtrl() { return this.creditReportForm?.controls?.borrowers?.get('currentAddress').get('state') };
    get currentAddressZipCtrl() { return this.creditReportForm?.controls?.borrowers?.get('currentAddress').get('zipCode') };
    get previousAddressStreetCtrl() { return this.creditReportForm?.controls?.borrowers?.get('previousAddresses').get('street') };
    get previousAddressCityCtrl() { return this.creditReportForm?.controls?.borrowers?.get('previousAddresses').get('city') };
    get previousAddressStateCtrl() { return this.creditReportForm?.controls?.borrowers?.get('previousAddresses').get('state') };
    get previousAddressZipCtrl() { return this.creditReportForm?.controls?.borrowers?.get('previousAddresses').get('zipCode') };
    get ccAddressStreetCtrl() { return this.creditReportForm?.controls?.billingInformation?.get('address').get('street') };
    get ccAddressCityCtrl() { return this.creditReportForm?.controls?.billingInformation?.get('address').get('city') };
    get ccAddressStateCtrl() { return this.creditReportForm?.controls?.billingInformation?.get('address').get('state') };
    get ccAddressZipCodeCtrl() { return this.creditReportForm?.controls?.billingInformation?.get('address').get('zipCode') };
    get ccCardNumberCtrl() { return this.creditReportForm?.controls?.billingInformation?.get('creditCard').get('cardNumber') };
    get ccExpirationCtrl() { return this.creditReportForm?.controls?.billingInformation?.get('creditCard').get('expiration') };
    get ccCvvCtrl() { return this.creditReportForm?.controls?.billingInformation?.get('creditCard').get('cvv') };
    get isBillingSameAsCurrentAddressCtrl() { return this.creditReportForm?.get('isBillingSameAsCurrentAddress') };
    get billingStreetCtrl() { return this.creditReportForm?.controls?.billingInformation?.get('address').get('street') };
    get billingCityCtrl() { return this.creditReportForm?.controls?.billingInformation?.get('address').get('city') };
    get billingStateCtrl() { return this.creditReportForm?.controls?.billingInformation?.get('address').get('state') };
    get billingZipCodeCtrl() { return this.creditReportForm?.controls?.billingInformation?.get('address').get('zipCode') };
    get isApplyingForCoborrowerCtrl() { return this.creditReportForm?.get('isApplyingForCoborrower') };
    get coBorrowerFirstNameCtrl() { return this.creditReportForm?.controls?.borrowers?.get('coBorrower').get('firstName') };
    get coBorrowerLastNameCtrl() { return this.creditReportForm?.controls?.borrowers?.get('coBorrower').get('lastName') };
    get coBorrowerSsnCtrl() { return this.creditReportForm?.controls?.borrowers?.get('coBorrower').get('ssn') };
    get coBorrowerBirthdateCtrl() { return this.creditReportForm?.controls?.borrowers?.get('coBorrower').get('birthDate') };
    get coBorrowerEmailCtrl() { return this.creditReportForm?.controls?.borrowers?.get('coBorrower').get('email') };
    get reportTypeCtrl() { return this.creditReportForm?.get('reportType')}
    constructor(@Inject(MAT_DIALOG_DATA) public data: any,
        public dialogRef: MatDialogRef<CreditReportDialogComponent>,
        public dialog: MatDialog,
        private fb: FormBuilder,
        private lgvLoanAppSvc: LgvLoanApplicationService,
        private notifSvc: NotificationService,
        private cdr: ChangeDetectorRef) {
        this.loanApp = data?.loanApp;
        this.taskId = data?.taskItem?.id;
        this.clientId = data?.clientId;
        this.taskItem = data?.taskItem;
        console.log(data?.taskItem)
    }

    ngOnInit(): void {
        const borrowers = this.loanApp?.loanInfo?.borrowers[0];
        const isApplyingForCoborrower = this.loanApp?.loanInfo?.customFields?.find(x => x?.code === 'IsApplyingForCoBorrower')?.value == 'true';
        const isCreditTermsAccepted = this.loanApp?.loanInfo?.customFields?.find(x => x?.code === 'CreditAuthorizationAccepted')?.value == 'true';
        const previousAddress = borrowers?.previousAddresses?.length > 0 ? borrowers?.previousAddresses[0] : null;
        this.creditReportForm = this.fb.group({
            reportType: [null, [Validators.required]],
            borrowers: this.fb.group({
                firstName: [borrowers?.firstName, [Validators.required]],
                middleName: [null],
                lastName: [borrowers?.lastName, [Validators.required]],
                ssn: [null, [Validators.required]],
                birthDate: [null, [Validators.required]],
                email: [borrowers?.contacts?.email, [Validators.required]],

                currentAddress: this.fb.group({
                    street: [borrowers?.currentAddress?.street, [Validators.required]],
                    city: [borrowers?.currentAddress?.city, [Validators.required]],
                    state: [borrowers?.currentAddress?.state, [Validators.required]],
                    zipCode: [borrowers?.currentAddress?.zipCode, [Validators.required]]
                }),
                previousAddresses: this.fb.group({
                    street: [previousAddress?.street],
                    city: [previousAddress?.city],
                    state: [previousAddress?.state],
                    zipCode: [previousAddress?.zipCode]
                }),
                coBorrower: this.fb.group({
                    firstName: [borrowers?.coBorrower?.firstName, isApplyingForCoborrower ? [Validators.required] :[]],
                    middleName: [null],
                    lastName: [borrowers?.coBorrower?.lastName, isApplyingForCoborrower ? [Validators.required] :[]],
                    ssn: [null, isApplyingForCoborrower ? [Validators.required] :[]],
                    birthDate: [null, isApplyingForCoborrower ? [Validators.required] :[]],
                    email: [borrowers.coBorrower?.contacts?.email, isApplyingForCoborrower ? [Validators.required] :[]]
                }),
            }),

            billingInformation: this.fb.group({
                address: this.fb.group({
                    street: [null, [Validators.required]],
                    city: [null, [Validators.required]],
                    state: [null, [Validators.required]],
                    zipCode: [null, [Validators.required]]
                }),
                creditCard: this.fb.group({
                    cardNumber: [null, [Validators.required, CreditCardValidators.validateCCNumber]],
                    expiration: [null, [Validators.required, Validators.minLength(9), CreditCardValidators.validateExpDate]],
                    cvv: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(4)]]
                })
            }),

            creditTerms: [isCreditTermsAccepted, [Validators.requiredTrue]],
            thirdPartyTerms: [false],

            // UI use only, not needed for request body
            isApplyingForCoborrower: [isApplyingForCoborrower, [Validators.required]],
            isCurrentAddressLessThanTwoYears: [false],
            isBillingSameAsCurrentAddress: [false, [Validators.required]],
        });
        
        this.isBillingSameAsCurrentAddressCtrl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((isSame) => {

            if(isSame){
                this.billingStreetCtrl.setValidators([]);
                this.billingCityCtrl.setValidators([]);
                this.billingStateCtrl.setValidators([]);
                this.billingZipCodeCtrl.setValidators([]);

                this.billingStreetCtrl.setValue(null);
                this.billingCityCtrl.setValue(null);
                this.billingStateCtrl.setValue(null);
                this.billingZipCodeCtrl.setValue(null);
            }else{
                this.billingStreetCtrl.setValidators([Validators.required]);
                this.billingCityCtrl.setValidators([Validators.required]);
                this.billingStateCtrl.setValidators([Validators.required]);
                this.billingZipCodeCtrl.setValidators([Validators.required]);
            }

            this.creditReportForm.updateValueAndValidity();
        });

        this.isApplyingForCoborrowerCtrl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((withCoborrower) => {
            if(withCoborrower) {
                this.coBorrowerFirstNameCtrl.setValidators([Validators.required])
                this.coBorrowerLastNameCtrl.setValidators([Validators.required])
                this.coBorrowerSsnCtrl.setValidators([Validators.required])
                this.coBorrowerBirthdateCtrl.setValidators([Validators.required])
                this.coBorrowerEmailCtrl.setValidators([Validators.required])
            } else {
                this.coBorrowerFirstNameCtrl.clearValidators();
                this.coBorrowerFirstNameCtrl.setErrors(null);
                this.coBorrowerLastNameCtrl.clearValidators();
                this.coBorrowerLastNameCtrl.setErrors(null);
                this.coBorrowerSsnCtrl.clearValidators();
                this.coBorrowerSsnCtrl.setErrors(null);
                this.coBorrowerBirthdateCtrl.clearValidators();
                this.coBorrowerBirthdateCtrl.setErrors(null);
                this.coBorrowerEmailCtrl.clearValidators();
                this.coBorrowerEmailCtrl.setErrors(null);
            }
            this.creditReportForm.updateValueAndValidity();
            this.creditReportForm.markAllAsTouched();
            this.cdr?.detectChanges();
        });

        this.reportTypeCtrl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((reportType)=> {
            this.calculateCreditReportTotalCost();
            this.creditReportForm.updateValueAndValidity();
            this.cdr?.detectChanges();
        });

        this.isApplyingForCoborrowerCtrl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((isApplyingForCoborrower) => {
            this.calculateCreditReportTotalCost();
        })
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(false);
        this.onDestroy$.complete();
        this.cdr?.detach();
    }

    onSubmit() {
        this.isLoading$.next(true);
        const requestBody = this.mapFormDataToRequestBody(this.creditReportForm.value);
        this.lgvLoanAppSvc.submitCreditReport(requestBody, this.loanApp?.id, this.taskId, this.clientId)
            .pipe(take(1), catchError((err) => {
                this.notifSvc.error("Submission failed. Please review your information and try again.");
                return of(false);
            }))
            .subscribe((response) => {
                this.isLoading$.next(false);
                if (response) {
                    this.notifSvc.success("Credit Report submitted.");
                    this.dialogRef.close({
                        action: true
                    });
                } else {
                    this.creditReportForm.updateValueAndValidity();
                }
            });
    }

    mapFormDataToRequestBody(formData: any) {
        const ccExpValue = formData.billingInformation.creditCard.expiration.toString();
        const ccExpParts = ccExpValue.split('-');
        const isCcExpAlreadyFormatted = ccExpParts.length === 2 && ccExpParts[0].length === 4 && ccExpParts[1].length === 2;

        formData.borrowers.birthDate = LgvTSHelper.jsDateToJsonDate(formData?.borrowers?.birthDate, true);
        formData.borrowers.previousAddresses = [
            formData.borrowers.previousAddresses
        ];
        formData.billingInformation.creditCard = {
            cardNumber: +formData.billingInformation?.creditCard.cardNumber.toString().replace(/\D/g, ''),
            expiration: formData.billingInformation.creditCard.expiration = isCcExpAlreadyFormatted ? ccExpValue : ccExpValue.slice(5, 9) + '-' + ccExpValue.slice(0, 2),
            cvv: formData.billingInformation.creditCard.cvv
        }
        if (!formData?.isApplyingForCoborrower) {
            formData.borrowers.coBorrower = null;
        } else {
            formData.borrowers.coBorrower.birthDate = LgvTSHelper.jsDateToJsonDate(formData?.borrowers?.coBorrower?.birthDate, true);
        }
        if (!formData?.isCurrentAddressLessThanTwoYears) {
            formData.borrowers.previousAddresses = [];
        }
        const mappedFormData = {
            reportType: formData.reportType,
            borrowers: [
                formData.borrowers
            ],
            billingInformation: {
                address: formData?.isBillingSameAsCurrentAddress ? formData?.borrowers.currentAddress : formData?.billingInformation?.address,
                creditCard: formData?.billingInformation?.creditCard
            }
        };

        // delete UI only related fields
        delete formData.isApplyingForCoborrower;
        delete formData.isCurrentAddressLessThanTwoYears;
        delete formData.isBillingSameAsCurrentAddress;

        return mappedFormData;
    }

    isValid(formData) {
        const cvvLength = formData?.billingInformation?.creditCard?.cvv?.toString().length ?? 0;

        return !!formData?.billingInformation?.creditCard.cardNumber &&
            !!formData?.billingInformation?.creditCard.cvv &&
            !!formData?.billingInformation?.creditCard.expiration &&
            this.ccCardNumberCtrl.errors == null &&
            this.ccExpirationCtrl.errors == null &&
            this.ccCvvCtrl.errors == null &&
            cvvLength >= 3;
    }

    openCreditInfoModal() {
        const viewTermsModal = this.dialog.open(ConfirmationDialogComponent,
            {
                data: {
                    Confirmation: `<h4>Credit Pull Report Types</h4>
                        <p>A soft credit pull is a preliminary, non-impactful check used during pre-qualification, 
                        while a hard credit pull is a detailed inquiry that affects your score, 
                        used before final loan approval.</p>
                        <p><a href="https://support.realfinity.io/hc/en-us/articles/29651431566356-What-is-the-Difference-Between-a-Soft-and-Hard-Credit-Pull"
                                    target="_blank">Read More <fa-icon icon="arrow-right"></fa-icon></a></p>`,
                    woCancelButton: true
                },
                width: '400px'
            }
        )
    }

    openViewTermsModal(key: string) {
        let confirmationText = '';
        switch(key) {
            case 'creditTerms':
                confirmationText = `<h2>Authorization To Run A Credit Check On Financial History</h2>
                <p>During your mortgage loan application process, this institution performs a credit check that requires us to obtain and confirm information regarding your personal and financial background. This information includes, but is not limited to, your past and present employment status, your deposit accounts, your past and present consumer credit record, your mortgage record, and\/or your rental record.<\/p><p>By clicking the \"I\/We agree to give authorization to run a credit check on financial history\" checkbox, I\/we hereby grant permission for us to obtain all information deemed necessary to process your mortgage loan application.<\/p>`;
                break;
        }
        const viewTermsModal = this.dialog.open(ConfirmationDialogComponent, 
            {
                data: {
                    Confirmation: confirmationText,
                    woCancelButton: true
                },
                width: '450px'
            }
        )
    }

    calculateCreditReportTotalCost() {
        const borrowerType = this.isApplyingForCoborrowerCtrl?.value ? 'Joint' : 'Individual';
        const amount = this.lgvLoanAppSvc.creditReportFees$.value?.fees?.find(
            fees => fees?.creditReportType === this.reportTypeCtrl?.value &&
                fees?.borrowerType === borrowerType
        )?.amount;
        const totalCost = ((amount) + (amount * this.lgvLoanAppSvc.creditReportFees$.value?.ccProcessingFeePercentage))
        this.creditReportTotalCost$.next(totalCost.toFixed(2));
    }

}
